import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { useDistrict } from "@/context/DistrictContext";
import { search as searchApi } from "@/lib/api";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
    { to: "/", label: "Portal", testid: "nav-portal" },
    { to: "/heritage-map", label: "Heritage Map", testid: "nav-map" },
    { to: "/dashboard", label: "Data Dashboard", testid: "nav-dashboard" },
];

export default function Header() {
    const { districts, slug, setSlug, district } = useDistrict();
    const [open, setOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const navigate = useNavigate();
    const debounceRef = useRef();

    useEffect(() => {
        clearTimeout(debounceRef.current);
        if (!query || query.length < 2) {
            debounceRef.current = setTimeout(() => setResults([]), 0);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const data = await searchApi(query, slug);
                setResults(data.results.slice(0, 8));
            } finally {
                setSearching(false);
            }
        }, 250);
    }, [query, slug]);

    const current = districts.find((d) => d.slug === slug) || district;

    return (
        <header className="sticky top-0 z-40 bg-ivory/85 backdrop-blur-md hairline-b" data-testid="site-header">
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center gap-4 md:gap-8">
                <NavLink to="/" className="shrink-0">
                    <Logo />
                </NavLink>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-7 ml-3" data-testid="primary-nav">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                                `font-data text-[11px] tracking-[0.18em] uppercase underline-reveal ${
                                    isActive ? "terra" : "ink"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex-1" />

                {/* Search */}
                <div className="relative hidden md:block w-64 xl:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ink-mute" />
                    <input
                        data-testid="search-input"
                        placeholder={`Search ${current?.name || "the district"}…`}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setTimeout(() => setOpen(false), 180)}
                        className="w-full pl-9 pr-3 py-2 text-sm font-body bg-transparent border hairline rounded-md focus:outline-none focus:border-[var(--ink)]"
                    />
                    {open && query.length >= 2 && (
                        <div className="absolute mt-2 left-0 right-0 bg-white hairline border rounded-md shadow-sm max-h-[420px] overflow-auto z-50" data-testid="search-results">
                            {searching && <div className="p-4 text-xs ink-mute font-data">Searching…</div>}
                            {!searching && results.length === 0 && (
                                <div className="p-4 text-xs ink-mute font-data">No results found.</div>
                            )}
                            {results.map((r, i) => {
                                const title = r.item.name || r.item.title || r.item.year || "Result";
                                return (
                                    <button
                                        key={i}
                                        data-testid={`search-result-${i}`}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            if (r.district_slug !== slug) setSlug(r.district_slug);
                                            navigate("/");
                                            setQuery("");
                                            setOpen(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 hover:bg-ivory-deep hairline-b last:border-b-0"
                                    >
                                        <div className="font-display text-base">{title}</div>
                                        <div className="font-data text-[10px] tracking-[0.14em] uppercase ink-mute mt-0.5">
                                            {r.section.replaceAll("_", " ")} · {r.district_name}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* District switcher */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid="district-switcher-button"
                            className="hidden md:flex items-center gap-2 px-3 py-2 border hairline rounded-md font-data text-xs tracking-[0.14em] uppercase hover:border-[var(--ink)] transition-colors"
                        >
                            <span className="w-2 h-2 rounded-full" style={{ background: current?.accent || "#C05640" }} />
                            {current?.name || "District"}
                            <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64 bg-white">
                        {districts.map((d) => (
                            <DropdownMenuItem
                                key={d.slug}
                                data-testid={`district-option-${d.slug}`}
                                onClick={() => setSlug(d.slug)}
                                className="cursor-pointer py-2.5"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="w-2 h-2 rounded-full" style={{ background: d.accent }} />
                                    <div>
                                        <div className="font-display text-base">{d.name}</div>
                                        <div className="font-data text-[10px] tracking-[0.14em] uppercase ink-mute">{d.state}</div>
                                    </div>
                                </div>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile menu trigger */}
                <button
                    data-testid="mobile-menu-trigger"
                    className="lg:hidden p-2"
                    onClick={() => setMobileOpen((o) => !o)}
                >
                    {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {/* Mobile menu */}
            {mobileOpen && (
                <div className="lg:hidden hairline-t bg-ivory px-5 py-5 space-y-4" data-testid="mobile-menu">
                    <nav className="flex flex-col gap-3">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`mobile-${n.testid}`}
                                onClick={() => setMobileOpen(false)}
                                className={({ isActive }) =>
                                    `font-display text-2xl ${isActive ? "terra" : "ink"}`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="pt-4 hairline-t">
                        <div className="font-data text-[10px] tracking-[0.18em] uppercase ink-mute mb-2">District</div>
                        <div className="flex flex-wrap gap-2">
                            {districts.map((d) => (
                                <button
                                    key={d.slug}
                                    data-testid={`mobile-district-${d.slug}`}
                                    onClick={() => { setSlug(d.slug); setMobileOpen(false); }}
                                    className={`chip ${d.slug === slug ? "bg-ink text-ivory" : ""}`}
                                >
                                    {d.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
